import { getProductCardIndexOnPage } from "~/components/product-card/helpers";
import { PRODUCT_CARD_INDEX } from "~/constants/local-storage";
import { Nullable } from "~/types/general.types";

interface Indexes {
  productCode: string;
  value?: number;
  expireAt: Date;
}

interface IndexInSession {
  url: string;
  value: number;
}

export const LOCAL_STORAGE_TTL_MIN = 2;

export const setIndexToLocalStorage = (
  productToPageMap: Map<string, number> | undefined,
  productCode: Nullable<string> | undefined,
) => {
  let savedIndexes = localStorage.getItem(PRODUCT_CARD_INDEX);

  if (!savedIndexes) {
    savedIndexes = JSON.stringify([]);
  }
  const indexesArray = JSON.parse(savedIndexes) as Indexes[];

  const clearIndexes = indexesArray.filter(
    (index) => new Date(index.expireAt) > new Date(),
  );

  const productIndex = clearIndexes.find(
    (index) => index.productCode === productCode,
  );
  const expireAt = new Date();
  expireAt.setMinutes(expireAt.getMinutes() + LOCAL_STORAGE_TTL_MIN);

  const nexIndex = {
    productCode: productCode || "",
    value: getProductCardIndexOnPage(productToPageMap, productCode),
    expireAt,
  };

  if (productIndex) {
    Object.assign(productIndex, nexIndex);
  } else {
    clearIndexes.push(nexIndex);
  }

  localStorage.setItem(PRODUCT_CARD_INDEX, JSON.stringify(clearIndexes));
};

export const getIndexFromLocalStorage = (
  productCode: string | undefined | null,
): number | undefined => {
  if (!productCode) {
    return undefined;
  }

  let savedIndexes = localStorage.getItem(PRODUCT_CARD_INDEX);

  if (!savedIndexes) {
    savedIndexes = JSON.stringify([]);
  }
  const indexesArray = JSON.parse(savedIndexes) as Indexes[];

  const productIndex = indexesArray.find(
    (index) => index.productCode === productCode,
  );

  if (!productIndex) {
    return undefined;
  }

  const updatedIndexes = indexesArray.filter(
    (index) => index.productCode !== productIndex.productCode,
  );
  localStorage.setItem(PRODUCT_CARD_INDEX, JSON.stringify(updatedIndexes));

  return productIndex.value;
};

export const setIndexToSessionStorage = (path?: string, productIndex?: number) => {
  if (!productIndex || !path) {
    return;
  }

  let savedIndexes = sessionStorage.getItem(PRODUCT_CARD_INDEX);

  if (!savedIndexes) {
    savedIndexes = JSON.stringify([]);
  }
  const indexesArray = JSON.parse(savedIndexes) as IndexInSession[];

  const productIndexToSession = {
    value: productIndex,
    url: path,
  } as IndexInSession;

  const productIndexFromSession = indexesArray.find((index) => index.url === path);

  if (productIndexFromSession) {
    Object.assign(productIndexFromSession, productIndexToSession);
  } else {
    indexesArray.push(productIndexToSession);
  }
  sessionStorage.setItem(PRODUCT_CARD_INDEX, JSON.stringify(indexesArray));
};

export const getIndexFromSessionStorage = (path?: string): number | undefined => {
  if (!path) {
    return;
  }

  const indexInSession = sessionStorage.getItem(PRODUCT_CARD_INDEX);
  if (!indexInSession) {
    return;
  }

  const indexes = JSON.parse(indexInSession) as IndexInSession[];

  const productIndex = indexes.find((index) => index.url === path);

  if (!productIndex) {
    return;
  }

  return productIndex.value;
};
